/**
 * GSAP Animations — Clarisse Ka
 * Patterns réutilisables pour animations au scroll et interactions
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Sélecteurs des éléments dont l'état initial caché est posé en CSS (classes Tailwind
// opacity-0 / scale-95 / translate). GSAP doit les rendre visibles SANS clearProps,
// sinon le style inline est supprimé et l'élément retombe sur la classe -> invisible.
const ANIMATED_SELECTOR =
  '.animate-fade-up, .animate-stagger-item, .animate-scale-in, .animate-slide-in-left, .animate-slide-in-right';

// Respecter prefers-reduced-motion
const prefersReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)'
).matches;

/**
 * Désactiver les animations si préférence utilisateur
 */
if (prefersReducedMotion) {
  // Forcer l'état final visible (override des classes opacity-0/scale-95/translate)
  gsap.set(ANIMATED_SELECTOR, { opacity: 1, x: 0, y: 0, scale: 1 });
} else {
  // .animate-fade-up n'a pas de classe opacity-0 en HTML : poser l'état caché tout de
  // suite évite le flash "visible puis disparaît" avant le déclenchement du ScrollTrigger.
  gsap.set('.animate-fade-up', { opacity: 0, y: 40 });

  /**
   * 1. FADE-UP — Apparition au scroll avec remontée légère
   * Usage: <section class="animate-fade-up">
   */
  gsap.utils.toArray('.animate-fade-up').forEach((el) => {
    gsap.fromTo(el,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          once: true,
        },
      }
    );
  });

  /**
   * 2. STAGGER GROUP — Items d'une grille qui s'animent en cascade
   * Usage: <div class="animate-stagger-group">
   *          <div class="animate-stagger-item opacity-0">...</div>
   *        </div>
   */
  gsap.utils.toArray('.animate-stagger-group').forEach((group) => {
    const items = group.querySelectorAll('.animate-stagger-item');
    if (!items.length) return;
    gsap.fromTo(items,
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        ease: 'power2.out',
        stagger: {
          amount: items.length > 6 ? 0.4 : 0.3,
          from: 'start',
        },
        scrollTrigger: {
          trigger: group,
          start: 'top 80%',
          once: true,
        },
      }
    );
  });

  /**
   * 3. SCALE-IN — Agrandissement progressif (cartes, images)
   * Usage: <div class="animate-scale-in scale-95 opacity-0">
   */
  gsap.utils.toArray('.animate-scale-in').forEach((el) => {
    gsap.fromTo(el,
      { scale: 0.95, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 0.7,
        ease: 'back.out(1.2)',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          once: true,
        },
    });
  });

  /**
   * 4. SLIDE-IN-LEFT / SLIDE-IN-RIGHT — Entrée horizontale
   * Usage: <div class="animate-slide-in-left -translate-x-8 opacity-0">
   *        <div class="animate-slide-in-right translate-x-8 opacity-0">
   */
  gsap.utils.toArray('.animate-slide-in-left').forEach((el) => {
    gsap.fromTo(el,
      { x: -40, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          once: true,
        },
      }
    );
  });

  gsap.utils.toArray('.animate-slide-in-right').forEach((el) => {
    gsap.fromTo(el,
      { x: 40, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          once: true,
        },
      }
    );
  });

  /**
   * 5. FLOAT — Animation douce en boucle (floats décoratives)
   * Usage: <div class="animate-float">
   */
  gsap.utils.toArray('.animate-float').forEach((el) => {
    gsap.to(el, {
      y: -10,
      duration: 3,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
    });
  });

  /**
   * 6. HOVER SCALE — Agrandissement au survol (service cards, etc.)
   * Usage: <div class="group-hover:scale-105" data-animate-hover>
   */
  document.querySelectorAll('[data-animate-hover]').forEach((el) => {
    el.addEventListener('mouseenter', () => {
      gsap.to(el, { scale: 1.05, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
    });
    el.addEventListener('mouseleave', () => {
      gsap.to(el, { scale: 1, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
    });
  });

  /**
   * 7. COUNT-UP — Chiffres clés qui montent
   * Usage: <span class="animate-count-up" data-target="100">0</span>
   */
  gsap.utils.toArray('[data-target]').forEach((el) => {
    const target = parseInt(el.dataset.target, 10);
    gsap.fromTo(el,
      { textContent: 0, opacity: 0 },
      {
      textContent: target,
      opacity: 1,
      duration: 2,
      ease: 'power2.out',
      snap: { textContent: 1 },
      scrollTrigger: {
        trigger: el,
        start: 'top 80%',
        once: true,
      },
      onUpdate() {
        el.textContent = Math.floor(el.textContent) + (el.dataset.suffix || '');
      },
      }
    );
  });

  /**
   * 8. LINE-DRAW — SVG stroke animation
   * Usage: <svg class="animate-line-draw">
   *          <path style="stroke-dasharray: 300; stroke-dashoffset: 300;">
   */
  gsap.utils.toArray('.animate-line-draw').forEach((svg) => {
    const paths = svg.querySelectorAll('path, line, circle, polyline');
    paths.forEach((path) => {
      const length = path.getTotalLength?.() || 0;
      if (length) {
        gsap.fromTo(path,
          { strokeDashoffset: length },
          {
          strokeDashoffset: 0,
          duration: 1.5,
          ease: 'power2.inOut',
          scrollTrigger: {
            trigger: svg,
            start: 'top 80%',
            once: true,
          },
          }
        );
      }
    });
  });

  /**
   * 9. PARALLAX LIGHT — Décalage léger au scroll
   * Usage: <div class="animate-parallax" data-parallax="0.5">
   */
  gsap.utils.toArray('[data-parallax]').forEach((el) => {
    const speed = parseFloat(el.dataset.parallax) || 0.3;
    gsap.to(el, {
      y: () => (window.innerHeight + el.offsetHeight) * speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        scrub: 1,
        start: 'top bottom',
        end: 'bottom top',
      },
    });
  });

  /**
   * 10. BUTTON CTA PULSE — Micro-animation d'appel à l'action
   * Usage: <button class="animate-pulse-cta">
   */
  gsap.utils.toArray('.animate-pulse-cta').forEach((btn) => {
    gsap.to(btn, {
      scale: 1.05,
      boxShadow: '0 0 20px rgba(196, 85, 106, 0.4)',
      duration: 0.5,
      ease: 'sine.inOut',
      repeat: 3,
      yoyo: true,
      delay: 3, // Commencer après 3s au chargement
    });
  });

  /**
   * 11. FILET DE SÉCURITÉ — Révéler les éléments « piégés »
   *
   * Les ScrollTrigger se déclenchent à `start: 'top 85%'`. Un élément situé
   * dans le dernier écran de la page ne peut jamais remonter au-dessus de
   * cette ligne (le scroll bute avant) : son trigger ne se déclenche JAMAIS
   * et il reste à opacity-0 → invisible définitivement. Fréquent en mobile
   * (viewport court → beaucoup plus de blocs piégés).
   *
   * On révèle immédiatement tout élément dont le scroll requis dépasse le
   * scroll maximum atteignable. À rejouer après load + au redimensionnement.
   */
  const revealTrapped = () => {
    const vh = window.innerHeight;
    const maxScroll = Math.max(
      0,
      document.documentElement.scrollHeight - vh
    );
    const triggerLine = vh * 0.85; // équivalent de start: 'top 85%'

    gsap.utils.toArray(ANIMATED_SELECTOR).forEach((el) => {
      if (gsap.getProperty(el, 'opacity') >= 0.99) return; // déjà révélé
      const top = el.getBoundingClientRect().top + window.scrollY;
      const neededScroll = top - triggerLine;
      if (neededScroll > maxScroll) {
        // Ligne de déclenchement inatteignable → révéler sans animation
        ScrollTrigger.getAll()
          .filter((t) => t.trigger === el || t.vars.trigger === el)
          .forEach((t) => t.kill());
        gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1 });
      }
    });
  };

  // Recalculer les positions une fois les images lazy chargées (évite des triggers
  // au-dessus de la ligne de flottaison qui ne se déclenchent jamais), puis
  // appliquer le filet de sécurité.
  window.addEventListener('load', () => {
    ScrollTrigger.refresh();
    revealTrapped();
  });

  // Le viewport peut changer (rotation mobile, redimensionnement) et déplacer
  // la frontière des éléments piégés.
  let resizeRaf;
  window.addEventListener(
    'resize',
    () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(revealTrapped);
    },
    { passive: true }
  );
}

// Cleanup sur navigation (important pour les SPA, utile aussi ici en sécurité).
// `pagehide` remplace `unload` (déprécié + casse le bfcache).
window.addEventListener('pagehide', () => {
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
});
